'use client';

import {useState, useEffect} from 'react';
import {useRouter} from 'next/navigation';
import {
  GlobalEventEmitter,
  Preferences,
  SKZUser,
  WebDeepLinkHandler,
  CurrentUserStore,
  CreateProfileScreenV2,
} from '@sdk-mobile';
import {UserInfo} from '@/services/internal/user/types';
import getGameId from '@/utils/getGameId';
import safeReturnTo from '@/utils/safeReturnTo';
import {SKILLZ_APP_GAME_ID} from '@/app/(app)/partner/[slug]/_src/shellGame';
import {setFtueCookie} from '@/utils/ftueCookieHelper';
import {FTUE_STATE} from '@/utils/constant';
import {useFirstFTUEGame} from '@/app/(app)/games/[gid]/_src/GameProviders/FTUEGameProvider';
import LoadingScreen from '@/app/(app)/games/[gid]/_src/LoadingScreen';

type Props = {
  user: UserInfo;
};


const CreateProfileContainer = ({user}: Props) => {
  const router = useRouter();
  const gameId = getGameId();
  const initialSKZUser = new SKZUser(user);
  const isFirstFTUEInProgress = useFirstFTUEGame();
  const [userSignedIn, setUserSignedIn] = useState(false);

  // Multicade SHELL (root/partner arcade, game 19369) create-profile is a "save your
  // account" step, NOT a game join — completing it should land back on the arcade
  // (returnTo, e.g. /store), never launch the FTUE practice match. The shell has no Unity
  // build, so that match can't launch anyway: it left the user stuck on a loading screen
  // trying to matchmake. Disable FTUE up front (the SAME bypass the deep-link path below
  // uses) so 'Next' completes → 'switchToTab' fires → we router.replace(returnTo). Real
  // games are untouched (their gid !== the shell id), so their first-run FTUE still runs.
  useEffect(() => {
    if (gameId === String(SKILLZ_APP_GAME_ID)) {
      Preferences.disableFTUE();
      setFtueCookie(FTUE_STATE.FINISHED);
    }
  }, [gameId]);

  useEffect(() => {
    const afterSignInSubscription = GlobalEventEmitter.addListener('UserSignedIn', () => {
      setUserSignedIn(true);
    });

    const redirectSubscription = GlobalEventEmitter.addListener('switchToTab', () => {
      // Deferred-auth resume (§9): if the visitor started signup from the arcade
      // (root/partner) it passes a same-origin `returnTo`; honour it so they land
      // back where they were instead of the game home. Opt-in — default unchanged.
      router.replace(safeReturnTo() ?? `/games/${gameId}/home`);
    });

    // Belt-and-suspenders for the SHELL arcade: disabling FTUE above should stop the
    // FTUE practice match from ever starting, but if the flow still emits it, catch it
    // and resume to `returnTo` instead of entering a match the buildless shell (19369)
    // can't launch (the "stuck matchmaking after create-profile" report). Real games
    // (gid !== shell id) are untouched — their first-FTUE match starts as normal.
    const ftueMatchSubscription = GlobalEventEmitter.addListener('StartFirstFTUEMatch', () => {
      if (gameId === String(SKILLZ_APP_GAME_ID)) {
        router.replace(safeReturnTo() ?? '/');
      }
    });

    const ftueSkippedSubscription = GlobalEventEmitter.addListener('SyncFTUESkipped', () => {
      Preferences.disableFTUE();
      setFtueCookie(FTUE_STATE.FINISHED);
    });

    return () => {
      afterSignInSubscription.remove();
      redirectSubscription.remove();
      ftueMatchSubscription.remove();
      ftueSkippedSubscription.remove();
    };
  }, [gameId, router]);

  /*
   * Check for a deep link matchCode in the URL on the server side.
   * If a valid matchCode is found, we disable the "2 z games" feature,
   * which in turn allows us to bypass the FTUE flow when the user clicks 'Next'
   * in the <CreateProfileScreenV2> component.
   */
  useEffect(() => {
    CurrentUserStore.clearUserCache(); // clears prev user data from localStorage
    WebDeepLinkHandler.checkDeepLink()
      .then((checkResult) => {
        if (checkResult) {
          Preferences.disableFTUE();
          setFtueCookie(FTUE_STATE.FINISHED);
        }
      })
      .catch((error) => {
        console.error('Deep link check failed:', error);
      });
  }, []);

  if (isFirstFTUEInProgress || userSignedIn) {
    return <LoadingScreen />;
  }

  return (
    <CreateProfileScreenV2
      initialUser={initialSKZUser}
      shouldAllowExit={false}
      shouldAllowPop={false}
      navigator={{}}
    />
  );
};

export default CreateProfileContainer;
